import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'

/**
 * Cognitive/Psychosocial Development subsection adapter
 * Handles communication, cognitive assessment, and substance abuse history (5 fields)
 */
export class CognitivePsychosocialAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Cognitive/Psychosocial assessment fields
    const communicationAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT
    )

    const cognitiveAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT
    )

    const historySubstanceAbuse = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE
    )?.at(0)

    const substanceAbuseNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT]: communicationAssessment,
      [RecertFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT]: cognitiveAssessment,
      [RecertFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE]: historySubstanceAbuse,
      [RecertFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES]: substanceAbuseNotes,
      [RecertFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT,
        value: this.getFormDataField(
          RecertFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT
        )
      })
    }

    if (this.formDataFieldExists(RecertFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT,
        value: this.getFormDataField(RecertFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT)
      })
    }

    if (this.formDataFieldExists(RecertFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE,
        value: this.getFormDataField(RecertFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES,
        value: this.getFormDataField(RecertFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY,
        value: this.getFormDataField(RecertFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
